// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, LogOut, User, ChevronDown, LayoutGrid } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export function TopNav({ onMenuClick }) {
  const { user, logout } = useAuth();
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-14 flex items-center justify-between px-4 bg-white border-b border-gray-200 shrink-0 z-20">
      {/* Left — mobile menu toggle */}
      <button
        onClick={onMenuClick}
        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 md:hidden"
        aria-label="Toggle menu"
      >
        <Menu size={20} />
      </button>

      {/* Centre — breadcrumb / app switcher */}
      <Link
        to="/apps"
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-600 transition-colors"
      >
        <LayoutGrid size={15} />
        <span>All Apps</span>
      </Link>

      {/* Right — user menu */}
      <div className="relative" ref={dropRef}>
        <button
          onClick={() => setDropOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center">
            <span className="text-xs font-semibold text-indigo-700">
              {user?.username?.[0]?.toUpperCase() ?? 'U'}
            </span>
          </div>
          <span className="hidden sm:block text-sm text-gray-700 font-medium max-w-[120px] truncate">
            {user?.username ?? user?.email ?? 'Account'}
          </span>
          <ChevronDown size={14} className="text-gray-400" />
        </button>

        {/* Dropdown */}
        {dropOpen && (
          <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl border border-gray-200 shadow-lg py-1 z-50">
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-800 truncate">
                {user?.username ?? '—'}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {user?.email ?? '—'}
              </p>
            </div>

            <Link
              to="/apps/profile"
              onClick={() => setDropOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <User size={14} className="text-gray-400" />
              Profile
            </Link>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
